import { useContext } from "react";
import ModalService from "../shared/bootstrap/modal/Modal";
import ToastContext from "../shared/bootstrap/Toast";
import FloatingPanelService from "../shared/FloatingPanel";
import { TranslationService } from "../../services/TranslationService";
import { RoleListGetResponse } from "./entities/RoleListGetResponse";

import Table, { TableHeader } from "../shared/Table";

import TableContext from "../task/TableContext";
import { DefaultModalProps } from "../shared/bootstrap/DefaultModal";
import TooltipComponent from "../shared/TooltipComponent";
import SearchInput from "../shared/components/SearchInput";
import { ButtonTooltipIcon } from "../shared/components/ButtonTooltipIcon";
import { RoleEdit } from "./RoleEdit";
import RoleTableProvider from "./RoleTableProvider";
import RoleService from "../../services/RoleService";
import CompanyService from "../../services/CompanyService";
import { getError } from "../../utils/RequestUtils";

const btnEditRoleClick = async (
  response: RoleListGetResponse | undefined,
  reload: () => void,
  title: string,
  id?: number,
  readonly?: boolean
) => {
  FloatingPanelService.showPanel({
    title: title,
    children: (
      <RoleEdit
        roleId={id}
        onSubmit={reload}
        readonly={readonly}
        roles={response?.list || []}
      />
    ),
    width: 900,
    height: 669
  });
};

const RoleList = () => {
  const { translate } = TranslationService;

  const tableHeaders: TableHeader[] = [
    new TableHeader("Name", translate.Name, false, false),
    new TableHeader("Actions", "", false, false)
  ];

  return (
    <RoleTableProvider>
      <div className="main-layout-container mx-3">
        <div className="main-panel">
          <h4 className="mb-2">{TranslationService.translate.Roles}</h4>
          <div className="card">
            <UserFilters />
            <Table
              emptyMessage={translate.NoResults}
              headers={tableHeaders}
              item={RoleTableItem}
            />
          </div>
        </div>
      </div>
    </RoleTableProvider>
  );
};

export const UserFilters = () => {
  const context = useContext(TableContext);

  const searchTerm = (value: string) => {
    context.applyFilters({ searchTerm: value });
  };

  return (
    <div className="genericHeader">
      <div className="w-100 ps-0">
        <div className="col-auto ps-0">
          <div className="actionButtonsHeader">
            {!context.loading && CompanyService.canDo("editrole") ? (
              <ButtonTooltipIcon
                withTitle
                title={TranslationService.translate.New}
                icon="fa-light fa-plus"
                onClick={() =>
                  btnEditRoleClick(
                    context.response.result,
                    context.reload,
                    TranslationService.translate.NewRole
                  )
                }
                isLink={false}
              />
            ) : null}
          </div>
        </div>
      </div>
      <SearchInput onSearch={searchTerm} autosearch={true} />
    </div>
  );
};

export const RoleTableItem = ({ data }: { data: RoleListGetResponse.Role }) => {
  const context = useContext(TableContext);
  const { showDefaultModal } = ModalService;
  const { showToast } = useContext(ToastContext);

  const btnDeleteUserClick = async (id: number) => {
    const modalProps: DefaultModalProps = {
      message: TranslationService.translate.ConfirmDeleteRolText,
      acceptButtonLabel: TranslationService.translate.Accept,
      onAcceptClick: async () => {
        const result = await RoleService.delete(id);
        if (result instanceof Error) {
          const err = getError(result);
          showToast(
            err.status === 400 && err.message
              ? err.message
              : TranslationService.translate.ErrorProcessingRequest,
            undefined,
            "danger"
          );
        } else {
          showToast(
            TranslationService.translate.RoleDeleted,
            undefined,
            "success"
          );
          ModalService.hideModal();
          context.reload();
        }
      }
    };
    showDefaultModal(modalProps);
  };

  return (
    <tr
      key={data.Name}
      className="show-child-on-hover"
      style={{ display: "flex", flex: 1 }}
    >
      <td>
        {data.Name ? data.Name : <i>{TranslationService.translate.Pending}</i>}
      </td>
      <td>
        <TooltipComponent
          title={TranslationService.translate.Preview}
          placement="right"
          className="align-middle"
        >
          <i
            className={
              "far fa-fw pointer fa-eye mx-2 show-when-hovering-parent"
            }
            onClick={() =>
              btnEditRoleClick(
                context.response.result,
                context.reload,
                TranslationService.translate.Rol + " " + data.Name,
                data.RoleID,
                true
              )
            }
          />
        </TooltipComponent>
        {CompanyService.canDo("editrole") && (
          <TooltipComponent
            title={TranslationService.translate.Edit}
            placement="right"
            className="align-middle"
          >
            <i
              className={
                "far fa-fw pointer fa-pen mx-2 show-when-hovering-parent"
              }
              onClick={() =>
                btnEditRoleClick(
                  context.response.result,
                  context.reload,
                  TranslationService.translate.Rol + " " + data.Name,
                  data.RoleID,
                  false
                )
              }
            />
          </TooltipComponent>
        )}
        {CompanyService.canDo("editrole") && (
          <TooltipComponent
            title={TranslationService.translate.Delete}
            placement="right"
            className="align-middle"
          >
            <i
              className={
                "far fa-fw pointer fa-trash text-danger mx-2 show-when-hovering-parent"
              }
              onClick={() => btnDeleteUserClick(data.RoleID)}
            />
          </TooltipComponent>
        )}
      </td>
    </tr>
  );
};

export default RoleList;
